import { useState } from 'react'

import { ApiError, api } from '../api'
import type { Gender, ProfilePublic, TimeAccuracy } from '../../contracts/api'
import { CityPicker } from '../components/CityPicker'
import type { City } from '../components/CityPicker'
import { haptic } from '../telegram'
import './onboarding.css'

type Step = 'promise' | 'birth' | 'interest'

const ACCURACY: { id: TimeAccuracy; title: string; note: string }[] = [
  { id: 'exact', title: 'Точно', note: 'знаю по документу' },
  { id: 'approx', title: 'Примерно', note: 'помню час-полтора' },
  { id: 'unknown', title: 'Не знаю', note: 'времени нет совсем' },
]

const GENDERS: { id: Gender; title: string }[] = [
  { id: 'female', title: 'Женский' },
  { id: 'male', title: 'Мужской' },
]

const INTERESTS: { id: string; title: string; note: string }[] = [
  { id: 'character', title: 'Характер', note: 'какой я на самом деле' },
  { id: 'relations', title: 'Отношения', note: 'как я рядом с людьми' },
  { id: 'work', title: 'Работа и деньги', note: 'где моя сила' },
  { id: 'periods', title: 'Периоды жизни', note: 'что сейчас за время' },
  { id: 'curiosity', title: 'Просто любопытно', note: 'посмотреть, что скажут карты' },
]

export function Onboarding({ onDone }: { onDone: (profile: ProfilePublic) => void }) {
  const [step, setStep] = useState<Step>('promise')
  const [birthDate, setBirthDate] = useState('')
  const [timeAccuracy, setTimeAccuracy] = useState<TimeAccuracy | null>(null)
  const [birthTime, setBirthTime] = useState('')
  const [city, setCity] = useState<City | null>(null)
  const [gender, setGender] = useState<Gender | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const birthReady =
    Boolean(birthDate) &&
    Boolean(city) &&
    Boolean(gender) &&
    Boolean(timeAccuracy) &&
    (timeAccuracy === 'unknown' || Boolean(birthTime))

  async function save(interest: string): Promise<void> {
    if (!city || !timeAccuracy || saving) return
    setSaving(true)
    setError('')
    try {
      const { profile } = await api.post<{ profile: ProfilePublic }>('/api/profile', {
        birthDate,
        birthTime: timeAccuracy === 'unknown' ? null : birthTime,
        timeAccuracy,
        // Координаты и часовой пояс сервер берёт сам из справочника: историю
        // переводов часов клиенту знать неоткуда.
        cityId: city.id,
        gender,
        interest,
      })
      haptic('medium')
      onDone(profile)
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Не получилось сохранить. Попробуй ещё раз')
      setSaving(false)
    }
  }

  if (step === 'promise') {
    return (
      <div className="screen no-tabs onboarding">
        <div className="topbar">
          <div className="brand">АТЛАС</div>
          <div className="topnote">шаг 1 из 3</div>
        </div>
        <h1 className="display">У тебя не одна карта</h1>
        <div className="sub">
          По дате, времени и месту рождения мы построим четыре редкие системы: Ба Цзы, Цзы Вэй Доу Шу,
          Цолькин и И-Цзин. Каждая опишет тебя по-своему — а дальше самое интересное: где они сходятся
          и где спорят.
        </div>
        <div className="promise-list">
          <div className="promise-item">
            <span className="promise-dot" style={{ background: 'var(--ochre)' }} />
            Ба Цзы — из чего ты сделан
          </div>
          <div className="promise-item">
            <span className="promise-dot" style={{ background: 'var(--bordo)' }} />
            Цзы Вэй — двенадцать сфер твоей жизни
          </div>
          <div className="promise-item">
            <span className="promise-dot" style={{ background: 'var(--turq)' }} />
            Цолькин — твой знак и ритм дней
          </div>
          <div className="promise-item">
            <span className="promise-dot" style={{ background: 'var(--milk)' }} />
            И-Цзин — ответ на конкретный вопрос
          </div>
        </div>
        <button type="button" className="btn btn-gold cta" onClick={() => setStep('birth')}>
          Начать
        </button>
        <div className="hint">Ничего не публикуется. Данные рождения видишь только ты.</div>
      </div>
    )
  }

  if (step === 'birth') {
    return (
      <div className="screen no-tabs onboarding">
        <div className="topbar">
          <div className="brand">АТЛАС</div>
          <div className="topnote">шаг 2 из 3</div>
        </div>
        <h1 className="display">Когда и где</h1>
        <div className="sub">
          Час рождения меняет половину карты. Если не помнишь — так и скажи: мы честно закроем то, что
          без него посчитать нельзя, и не будем выдумывать.
        </div>

        <div className="field-group">
          <div className="lbl">Дата рождения</div>
          <input
            className="field"
            type="date"
            min="1900-01-01"
            max="2030-12-31"
            value={birthDate}
            onChange={(event) => setBirthDate(event.target.value)}
          />
        </div>

        <div className="field-group">
          <div className="lbl">Время рождения</div>
          <div className="choice-row">
            {ACCURACY.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`choice${timeAccuracy === option.id ? ' active' : ''}`}
                onClick={() => {
                  haptic('light')
                  setTimeAccuracy(option.id)
                }}
              >
                <span className="choice-title">{option.title}</span>
                <span className="choice-note">{option.note}</span>
              </button>
            ))}
          </div>
          {timeAccuracy && timeAccuracy !== 'unknown' && (
            <input
              className="field"
              type="time"
              value={birthTime}
              onChange={(event) => setBirthTime(event.target.value)}
            />
          )}
          {timeAccuracy === 'unknown' && (
            <div className="field-warn">
              Без времени Цзы Вэй Доу Шу не строится, а в Ба Цзы будет три столпа из четырёх. Всё
              остальное посчитаем полностью.
            </div>
          )}
        </div>

        <div className="field-group">
          <div className="lbl">Место рождения</div>
          <CityPicker value={city} onPick={setCity} />
        </div>

        <div className="field-group">
          <div className="lbl">Пол</div>
          <div className="choice-row">
            {GENDERS.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`choice${gender === option.id ? ' active' : ''}`}
                onClick={() => {
                  haptic('light')
                  setGender(option.id)
                }}
              >
                <span className="choice-title">{option.title}</span>
              </button>
            ))}
          </div>
          <div className="field-note-inline">Нужен ровно для одного: в Ба Цзы от него зависит, в какую сторону идут большие периоды жизни.</div>
        </div>

        <button
          type="button"
          className="btn btn-gold cta"
          disabled={!birthReady}
          onClick={() => setStep('interest')}
        >
          Дальше
        </button>
      </div>
    )
  }

  return (
    <div className="screen no-tabs onboarding">
      <div className="topbar">
        <div className="brand">АТЛАС</div>
        <div className="topnote">шаг 3 из 3</div>
      </div>
      <h1 className="display">С чего начнём</h1>
      <div className="sub">Это меняет порядок, в котором Атлас будет тебе открываться. На сами расчёты не влияет.</div>
      <div className="rowlist interest-list">
        {INTERESTS.map((option) => (
          <button
            key={option.id}
            type="button"
            className="rowitem"
            disabled={saving}
            onClick={() => void save(option.id)}
          >
            <div>
              <div className="ttl">{option.title}</div>
              <div className="note">{option.note}</div>
            </div>
            <div className="right">›</div>
          </button>
        ))}
      </div>
      {saving && <div className="state">Строим твои карты…</div>}
      {error && <div className="field-warn">{error}</div>}
    </div>
  )
}
